/**
 * Cortex Details Process
 * 
 * Shows detailed information about a selected cortex in a view.
 * Spawns when a cortex is clicked, dismisses when the cortex is unfocused.
 */

import type { ProcessContext } from '../../systems/process.js';
import { ProcessPriority } from '../../systems/process.js';
import { BaseProcess } from '../../systems/base-process.js';
import { BaseViewHTML } from '../../systems/base-view-html.js';
import { registerProcess } from '../../systems/registry.js';
import { bus } from '../../core/events.js';
import { log } from '../../services/logging/index.js';
import { cortexBridge } from '../../services/cortex-bridge/service.js';
import { 
  designTokens, 
  createHeading, 
  createContainer,
  createListItem
} from '../../core/design-system.js';

// Import cortex data (we'll need to access CORTEX_NODES, CORTEX_DESCRIPTIONS, getCortexDependencies)
// These are exported from the cortexNetwork module
interface CortexDetailsData {
  cortexId: string;
  label: string;
  description: string;
  category: string;
  wave: number;
  active: boolean;
  dependencies: {
    required: string[];
    optional: string[];
  };
  dependencyLabels: {
    required: string[];
    optional: string[];
  };
  lastExecution?: {
    timestamp: number;
    confidence: number;
    summary?: string;
    output?: any;
  };
}

export class CortexDetailsProcess extends BaseProcess {
  kind: string = 'cortex-details';

  metadata = {
    priority: ProcessPriority.NORMAL,
    category: 'utility' as const,
    canStop: true,
    preserveState: false,
    timeSensitive: false
  };

  private cortexDetailsView: CortexDetailsView | null = null;
  private currentCortexId: string | null = null;

  constructor(id: string) {
    super(id);
  }

  protected onStart(ctx: ProcessContext): void {
    log.debug(`[CortexDetailsProcess] Starting process ${this.id}`);
    this.context = ctx;

    // Listen for cortex focus events
    bus.on('cortex.focused', (payload: { cortexId: string; label: string }) => {
      this.showCortexDetails(ctx, payload.cortexId, payload.label);
    });

    // Listen for cortex unfocus events
    bus.on('cortex.unfocused', () => {
      this.hideCortexDetails(ctx);
    });

    // Listen for live cortex execution events
    bus.on('cortex.response', (event: any) => {
      if (this.currentCortexId === event.cortex && this.cortexDetailsView) {
        this.updateCortexExecution(event);
      }
    });

    bus.emit('process.started', { processId: this.id, kind: this.kind });
  }

  protected onStop(): void {
    log.debug(`[CortexDetailsProcess] Stopping process ${this.id}`);
    if (this.cortexDetailsView && this.context) {
      this.context.destroyView(this.cortexDetailsView.id);
    }
    this.cortexDetailsView = null;
    this.currentCortexId = null;
  }

  private updateCortexExecution(event: any): void {
    if (!this.cortexDetailsView) return;
    
    const currentData = this.cortexDetailsView.getCortexData();
    const updatedData: CortexDetailsData = {
      ...currentData,
      lastExecution: {
        timestamp: event.timestamp,
        confidence: event.confidence,
        output: event.output,
        summary: event.summary
      }
    };
    
    this.cortexDetailsView.updateData(updatedData);
  }

  private async showCortexDetails(ctx: ProcessContext, cortexId: string, label: string): Promise<void> {
    // If already showing this cortex, don't recreate
    if (this.currentCortexId === cortexId && this.cortexDetailsView) {
      return;
    }

    // Hide previous view if different cortex
    if (this.cortexDetailsView && this.currentCortexId !== cortexId) {
      this.hideCortexDetails(ctx);
    }

    // Load cortex data
    const cortexData = await this.loadCortexData(cortexId, label);
    if (!cortexData) {
      log.warn(`[CortexDetailsProcess] Could not load data for cortex: ${cortexId}`);
      return;
    }

    // Create new view
    const view = new CortexDetailsView(
      this.id,
      `${this.id}-main`,
      cortexData,
      this
    );
    const viewInstance = view.toViewInstance();
    this.cortexDetailsView = view;
    this.currentCortexId = cortexId;
    
    ctx.registerView(viewInstance);
    this.views.set(viewInstance.id, viewInstance);
    
    log.debug(`[CortexDetailsProcess] Spawned cortex details view for: ${cortexId}`);
  }

  private hideCortexDetails(ctx: ProcessContext): void {
    if (this.cortexDetailsView && this.context) {
      this.context.destroyView(this.cortexDetailsView.id);
      this.views.delete(this.cortexDetailsView.id);
    }
    this.cortexDetailsView = null;
    this.currentCortexId = null;
  }

  private async loadCortexData(cortexId: string, label: string): Promise<CortexDetailsData | null> {
    try {
      // Import cortex data from the network module
      const { CORTEX_NODES, CORTEX_DESCRIPTIONS, getCortexDependencies } = await import('../../services/majaku-privacy-background/scene/cortexNetwork.js');
      
      // Get cortex node data
      const cortexNode = CORTEX_NODES.find((n: any) => n.id === cortexId);
      if (!cortexNode) {
        return null;
      }

      // Get description
      const description = CORTEX_DESCRIPTIONS[cortexId] || 'No description available.';

      // Get dependencies
      const dependencies = getCortexDependencies(cortexId);

      // Get dependency labels
      const dependencyLabels = {
        required: dependencies.required.map(id => {
          const node = CORTEX_NODES.find((n: any) => n.id === id);
          return node?.label || id;
        }),
        optional: dependencies.optional.map(id => {
          const node = CORTEX_NODES.find((n: any) => n.id === id);
          return node?.label || id;
        })
      };

      return {
        cortexId,
        label: cortexNode.label || label,
        description,
        category: cortexNode.category || 'unknown',
        wave: cortexNode.wave || 0,
        active: cortexNode.active !== false,
        dependencies,
        dependencyLabels
      };
    } catch (e) {
      log.error(`[CortexDetailsProcess] Failed to load cortex data:`, e);
      return null;
    }
  }
}

/**
 * Cortex Details View
 * 
 * Displays detailed information about a cortex.
 */
class CortexDetailsView extends BaseViewHTML {
  constructor(
    processId: string,
    viewId: string,
    private cortexData: CortexDetailsData,
    private process: CortexDetailsProcess
  ) {
    super(processId, viewId, {
      sizes: [1, 3], // [1] = full+standard (default), [3] = full+double
      showDismissButton: true,
      resizable: true,
    });
  }

  getCortexData(): CortexDetailsData {
    return this.cortexData;
  }

  updateData(newData: CortexDetailsData): void {
    this.cortexData = newData;
    this.markDirty();
  }

  private executeCortex(): void {
    const { cortexId, label } = this.cortexData;
    log.info(`[CortexDetailsView] Executing cortex: ${cortexId}`);
    
    // Optimistic update (optional, or just wait for stream)
    bus.emit('cortex.status', { id: cortexId, status: 'active', timestamp: Date.now() });
    
    cortexBridge.sendIntent('EXECUTE_CORTEX', { 
      cortexId, 
      context: { source: 'details-view' } 
    });
  }

  protected render(): void {
    super.render();
    
    if (this.container) {
      const btn = this.container.querySelector('.execute-cortex-btn');
      if (btn) {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.executeCortex();
        });
      }
    }
  }

  protected getContentHTML(): string {
    const { cortexId, label, description, category, wave, active, dependencies, dependencyLabels, lastExecution } = this.cortexData;
    
    const statusColor = active ? designTokens.accent : '#666';
    const statusText = active ? '' : ' | PLANNED';

    // Build action section
    const actionSection = active ? `
      <div style="margin-bottom: ${designTokens.padding.lg};">
        <button class="execute-cortex-btn" style="
          background: rgba(0, 255, 136, 0.1);
          border: 1px solid ${designTokens.accent};
          color: ${designTokens.accent};
          padding: ${designTokens.padding.sm} ${designTokens.padding.md};
          border-radius: 4px;
          cursor: pointer;
          font-family: inherit;
          font-size: ${designTokens.fontSize.sm};
          text-transform: uppercase;
          letter-spacing: 1px;
          transition: all 0.2s ease;
          width: 100%;
        " onmouseover="this.style.background='rgba(0, 255, 136, 0.2)'" 
           onmouseout="this.style.background='rgba(0, 255, 136, 0.1)'">
          Execute Cortex
        </button>
      </div>
    ` : '';

    // Build live execution section
    const liveSection = lastExecution ? `
      <div style="
        margin-bottom: ${designTokens.padding.lg};
        padding: ${designTokens.padding.md};
        background: rgba(0, 255, 136, 0.05);
        border: 1px solid rgba(0, 255, 136, 0.2);
        border-radius: 4px;
      ">
        <div style="
          font-size: ${designTokens.fontSize.xs};
          color: #00ff88;
          font-weight: 600;
          text-transform: uppercase;
          margin-bottom: ${designTokens.padding.xs};
          display: flex;
          justify-content: space-between;
        ">
          <span>Live Execution</span>
          <span>${new Date(lastExecution.timestamp).toLocaleTimeString()}</span>
        </div>
        <div style="
          font-size: ${designTokens.fontSize.sm};
          color: ${designTokens.contentColor};
        ">
          ${lastExecution.summary || (lastExecution.output ? JSON.stringify(lastExecution.output).slice(0, 100) + '...' : 'Executed')}
        </div>
        <div style="
          margin-top: ${designTokens.padding.xs};
          font-size: ${designTokens.fontSize.xs};
          color: rgba(255, 255, 255, 0.5);
        ">
          Confidence: ${Math.round(lastExecution.confidence * 100)}%
        </div>
      </div>
    ` : '';

    // Build dependencies section
    const depsContent = dependencies.required.length > 0 || dependencies.optional.length > 0
      ? `
        ${dependencies.required.length > 0 ? `
          ${createListItem(`
            <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.xs};">
              <div style="font-size: ${designTokens.fontSize.xs}; color: #00ff88; font-weight: 500;">
                Required:
              </div>
              <div style="font-size: ${designTokens.fontSize.sm}; color: ${designTokens.contentColor};">
                ${dependencyLabels.required.join(', ')}
              </div>
            </div>
          `, { variant: 'subtle' })}
        ` : ''}
        ${dependencies.optional.length > 0 ? `
          ${createListItem(`
            <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.xs};">
              <div style="font-size: ${designTokens.fontSize.xs}; color: #00aaff; font-weight: 500;">
                Optional:
              </div>
              <div style="font-size: ${designTokens.fontSize.sm}; color: ${designTokens.contentColor};">
                ${dependencyLabels.optional.join(', ')}
              </div>
            </div>
          `, { variant: 'subtle' })}
        ` : ''}
      `
      : createListItem(`
          <div style="font-size: ${designTokens.fontSize.sm}; color: ${designTokens.contentColorMuted}; font-style: italic;">
            No dependencies
          </div>
        `, { variant: 'subtle' });

    const heading = createHeading(2, label, { 
      margin: `0 0 ${designTokens.padding.md} 0`,
      style: `color: ${statusColor}; text-transform: uppercase; letter-spacing: 1px;`
    });

    const metadata = `
      <div style="
        font-size: ${designTokens.fontSize.sm};
        color: ${statusColor};
        margin-bottom: ${designTokens.padding.md};
        opacity: 0.8;
        font-family: monospace;
      ">
        Category: ${category.toUpperCase()} | Wave: ${wave}${statusText}
      </div>
    `;

    const descriptionSection = `
      <div style="
        font-size: ${designTokens.fontSize.md};
        color: ${designTokens.contentColor};
        line-height: 1.6;
        margin-bottom: ${designTokens.padding.lg};
      ">
        ${this.escapeHtml(description)}
      </div>
    `;

    const dependenciesSection = `
      <div style="margin-top: ${designTokens.padding.lg};">
        <h3 style="
          margin: 0 0 ${designTokens.padding.md} 0;
          font-size: ${designTokens.fontSize.sm};
          font-weight: 600;
          color: ${designTokens.accent};
          text-transform: uppercase;
          letter-spacing: 1px;
        ">
          Dependencies
        </h3>
        <ul style="list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: ${designTokens.padding.sm};">
          ${depsContent}
        </ul>
      </div>
    `;

    return createContainer(
      heading + metadata + actionSection + liveSection + descriptionSection + dependenciesSection,
      { padding: 'lg', flexDirection: 'column' }
    );
  }

  protected escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

registerProcess('cortex-details', (id: string) => new CortexDetailsProcess(id));

